import {makeAdUrl} from '@frontend/helpers'
import {AdLikeIconButton} from '@frontend/modules/public/ads/components/list/AdLikeIconButton'
import {AdPreviewPhotosSwiper} from '@frontend/modules/public/ads/components/list/AdPreviewPhotosSwiper'
import {useUiLayoutStore} from '@frontend/stores'
import {mdiFire} from '@mdi/js'
import {useClampText} from '@shared/helpers/useClampText'
import clsx from 'clsx'
import type {
    CSSProperties,
    MouseEvent,
    RefObject,
} from 'react'
import {Link} from 'react-router-dom'
import {Icon} from 'swayok-react-mdb-ui-kit/components/Icon/Icon'
import {useEventCallback} from 'swayok-react-mdb-ui-kit/helpers/useEventCallback'
import {AnalyticsEventReporter} from '../../../../../helpers/AnalyticsEventReporter'
import type {AdPreviewDataForGrid} from '../../AdsModuleTypes'
import {AdPrice} from './AdPrice'

interface AdsGridItemProps {
    ad: AdPreviewDataForGrid
    example?: boolean
    className?: string
    gridOrder?: number
    urlTarget?: '_blank' | '_self'
    style?: CSSProperties
}

// Карточка предпросмотра объявления.
export function AdsGridItem(props: AdsGridItemProps) {

    const {
        className,
        ad,
        example,
        urlTarget = '_blank',
        style = {},
        gridOrder = 0,
    } = props

    const bootstrapSize = useUiLayoutStore(
        state => state.bootstrapSize
    )

    const [
        adTitleRef,
        {clampedText: adTitle, key: adTitleKey},
    ] = useClampText({
        text: ad.title,
        // На узких экранах карточка вдвое уже, поэтому названию отдано
        // больше строк: по макету на десктопе 2 строки, на мобильной 4.
        lines: bootstrapSize === 'xxs' || bootstrapSize === 'xs' ? 4 : 2,
        ellipsis: '...',
        expanded: false,
    })

    const [
        locationRef,
        {clampedText: location, key: locationKey},
    ] = useClampText({
        text: ad.location ?? '',
        lines: 2,
        ellipsis: '...',
        expanded: false,
    })

    // Обработчик нажатия на ссылку объявления.
    const onClick = useEventCallback((
        event: MouseEvent<HTMLElement>
    ) => {
        if (example) {
            event.preventDefault()
        }
        AnalyticsEventReporter.clickOnAdFromList(ad)
    })

    return (
        <div
            className={clsx(
                'offered-item-card presentation-grid flex-container-column',
                ad.isPromoted ? 'promoted' : null,
                className
            )}
            style={{
                ...style,
                order: gridOrder,
            }}
        >
            <div className="offered-item-card-image-and-icons position-relative">
                {ad.isPromoted && (
                    <div className="offered-item-card-promoted-label bg-orange rounded-end ps-1 pe-2 fs-7 with-icon-flex fw-500 text-dark">
                        <Icon
                            path={mdiFire}
                            size={20}
                            color="dark"
                        />
                        HOT!
                    </div>
                )}
                <AdPreviewPhotosSwiper ad={ad} />
            </div>
            <div className="offered-item-card-info flex-container-column gap-2 mt-3">
                <div className="d-flex flex-row align-items-start justify-content-between gap-3">
                    <Link
                        className="offered-item-card-title fw-500 lh-sm link-dark link-hover-default"
                        target={urlTarget}
                        to={makeAdUrl(ad.slugWithId, ad.categorySlugWithId)}
                        rel="noreferrer"
                        ref={adTitleRef as RefObject<HTMLAnchorElement>}
                        key={adTitleKey}
                        onClick={onClick}
                    >
                        {ad.photos.length > 0 && (
                            // Для поисковиков.
                            <img
                                src={ad.photos[0]}
                                className="position-absolute opacity-0"
                                width={1}
                                height={1}
                                alt={ad.title}
                            />
                        )}
                        {adTitle}
                    </Link>
                    <AdLikeIconButton
                        ad={ad}
                        isFavourite={ad.isFavourite}
                        noLabel
                        disabled={example}
                        className="offered-item-card-like"
                    />
                </div>
                <AdPrice
                    price={ad.price}
                    previousPrice={ad.previousPrice}
                />
                {ad.location && (
                    <div
                        className="offered-item-card-location fs-7 fw-500 text-gray"
                        ref={locationRef as RefObject<HTMLDivElement>}
                        key={locationKey}
                    >
                        {location}
                    </div>
                )}
            </div>
        </div>
    )
}
